import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Navbar from "react-bootstrap/Navbar";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import { logout } from "../utils/authService";
import { useAppContext } from "../AppContext";
import { getMobileNavbarHeading, getNavbarHeading } from "../constants/header";
import { openSidebar } from "../store/sidebar";
import {
  clearAllUser,
  getUserInformation,
  getUserRole,
} from "../store/userRole";
import { useNavigate, useParams } from "react-router-dom";
import SubscriptionModal from "./SubscriptionModal";
import { Image, Spinner } from "react-bootstrap";
import { resetCategories } from "../store/categoriesSlice";
import FaUser from "../constants/icons/Fa/FaUser";
import MdMenu from "../constants/icons/Md/MdMenu";
import { getCurrentGreetingMessage } from "../utils/greetingMessage";
import { useQuery } from "react-query";
import { fetchSubscriptionByUserId } from "../store/subscriptionSlice";
import NotificationPopover from "./NotificationPopover/NotificationPopover";
// import useWebSocket from "react-use-websocket";
import {persistor} from "../store";

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </div>
));

const Header = ({ isMobileView }) => {
  const [showModal, setShowModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState("loading"); // 'loading', 'trialExpired', 'packageExpired', 'active'

  const navigate = useNavigate();
  const { dispatch } = useAppContext();
  const storeDispatch = useDispatch();
  const navbar = useSelector((state) => state.navbar);
  const roleOfTheUser = useSelector(getUserRole);
  const userInformation = useSelector(getUserInformation);
  const { id: classroomId } = useParams();
  const dropdownRef = useRef(null);
  // const WS_URL = `${process.env.REACT_APP_SOCKET_URL.slice(
  //   0,
  //   process.env.REACT_APP_SOCKET_URL.length - 1
  // )}?userId=${userInformation?._id}`;
  // const { lastJsonMessage } = useWebSocket(WS_URL, {
  //   share: false,
  //   shouldReconnect: () => true,
  // });
  // const [realTimeNotification, setRealTimeNotification] = useState(null);

  // // Run when a new WebSocket message is received (lastJsonMessage)
  // useEffect(() => {
  //   if (lastJsonMessage && lastJsonMessage?.notification) {
  //     setRealTimeNotification(lastJsonMessage?.notification);
  //   }
  // }, [lastJsonMessage]);

  const handleLogout = async () => {
    dispatch({ type: "SET_AUTH_STATUS", payload: false });
    storeDispatch(clearAllUser());
    storeDispatch(resetCategories());
    localStorage.removeItem('classroomPageNo');
    persistor.purge()
    localStorage.removeItem('persist:root')
    await logout();
  };

  const showSidebar = () => {
    storeDispatch(openSidebar(true));
  };

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  // Use react-query to fetch subscription data
  const {
    data: subscription,
    isLoading,
    isError,
  } = useQuery(
    ["subscriptionData", classroomId, userInformation?._id],
    () => fetchSubscriptionByUserId(classroomId, userInformation?._id),
    {
      staleTime: 60000, // Cache data for 60 seconds
      cacheTime: 1000 * 60 * 10, // Cache data for 10 minutes
      refetchInterval: 120000, // Refetch every 120 seconds
      enabled: roleOfTheUser === "Student" && !!classroomId, // Disable query if within the first 24 hours
      retry: false, // No retries if an error occurs
    }
  );

  const calculateRemainingTime = useCallback(() => {
    const currentDate = new Date();
    if (userInformation) {
      const currentClassroom = userInformation?.classrooms?.find(
        (classroom) => classroom?._id === classroomId
      );

      if (currentClassroom) {
        const creationDate = new Date(currentClassroom.createdAt);
        const diffInMilliseconds = currentDate - creationDate;
        const hoursDiff = diffInMilliseconds / (1000 * 60 * 60);

        // Condition 1: Within the first 24 hours, no subscription
        if (hoursDiff <= 24 && (!subscription || subscription?.error)) {
          const trialEndDate = new Date(
            creationDate.getTime() + 24 * 60 * 60 * 1000
          );
          setSubscriptionStatus("activeTrial");
          return getTimeDifference(trialEndDate, currentDate);
        }
      }

      // Condition 3: Subscription is valid
      if (subscription && !subscription?.error) {
        const endDate = new Date(subscription.endDate);
        endDate.setHours(23, 59, 59, 999);
        const remainingTime = getTimeDifference(endDate, currentDate);
        if (
          remainingTime.days === 0 &&
          remainingTime.hours === 0 &&
          remainingTime.minutes === 0 &&
          remainingTime.seconds === 0
        ) {
          setSubscriptionStatus("packageExpired"); // Condition 4: Subscription expired
        } else {
          setSubscriptionStatus("active"); // Condition 3: Subscription valid
        }
        return remainingTime;
      }
    }

    // Condition 2: Trial expired
    if (isError || (subscription && subscription?.error)) {
      setSubscriptionStatus("trialExpired");
    }

    return null;
  }, [subscription, isError, userInformation, classroomId]);

  const getTimeDifference = (endDate, currentDate) => {
    const diffTime = Math.max(endDate - currentDate, 0);

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffTime % (1000 * 60)) / 1000);

    return {
      days: diffDays,
      hours: diffHours,
      minutes: diffMinutes,
      seconds: diffSeconds,
    };
  };

  useEffect(() => {
    setRemainingTime(calculateRemainingTime());
  }, [calculateRemainingTime]);

  const renderRemainingTime = useCallback(() => {
    if (isLoading) {
      return <Spinner animation="border" />;
    }

    // PRIORITY 1: Check if subscription has hourly plan with remaining hours
    if (subscription && subscription?.totalHoursOfStudent !== undefined && subscription?.totalHoursOfStudent !== null) {
      const totalHours = parseFloat(subscription.totalHoursOfStudent) || 0;
      const consumedHours = parseFloat(subscription.hoursConsumedOfStudent) || 0;
      const hoursLeft = totalHours - consumedHours;

      if (hoursLeft <= 0) {
        return "Hourly package expired";
      }
      if (hoursLeft % 1 === 0) {
        return `${hoursLeft} hour${hoursLeft !== 1 ? "s" : ""} left`;
      } else {
        return `${hoursLeft.toFixed(1)} hours left`;
      }
    }

    // PRIORITY 2: Fall back to date-based logic
    if (subscriptionStatus === "trialExpired") {
      return "Trial expired";
    }

    if (subscriptionStatus === "packageExpired") {
      return "Package expired";
    }

    const { days, hours, minutes, seconds } = remainingTime || {};

    // When time is expired
    const isExpired =
      days === 0 && hours === 0 && minutes === 0 && seconds === 0;

    if (isExpired) {
      return "Your package has expired.";
    }

    // Display days remaining if there are days left
    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} left`;
    }

    // If hours remain, display hours
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} left`;
    }

    // If no hours remain, but minutes are left, display minutes
    if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""} left`;
    }

    // If no hours and minutes remain, but seconds are left, display seconds
    if (seconds > 0) {
      return `${seconds} second${seconds > 1 ? "s" : ""} left`;
    }

    return "Loading...";
  }, [remainingTime, isLoading, subscriptionStatus, subscription]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(calculateRemainingTime());
    }, 1000); // Update every second

    return () => clearInterval(interval); // Clear the interval on unmount
  }, [calculateRemainingTime]);

  return (
    <>
      <Navbar
        style={{ color: "black" }}
        className={`justify-content-between top-screen-navbar ${
          isMobileView && "d-none"
        } px-5`}
      >
        <div className="d-flex justify-content-center w-100 align-items-center">
          <Col sm={9}>
            {navbar?.text ? (
              getNavbarHeading(navbar?.text, navbar?.params)
            ) : (
              <h3
                className={`mb-0 top-heading header-title`}
                style={{ fontSize: "20px", color: "#1D2026" }}
              >
                {getCurrentGreetingMessage()}!
              </h3>
            )}
          </Col>
          <Col
            sm={3}
            className="d-flex align-items-center justify-content-end gap-2"
          >
            <div className="subscription-info">
              {roleOfTheUser === "Student" && classroomId !== undefined && (
                <div
                  className="subscription-box subscription-box-width"
                  onClick={handleModalOpen}
                >
                  {renderRemainingTime()}
                </div>
              )}
              <NotificationPopover
                isMobileView={isMobileView}
                // realTimeNotification={realTimeNotification}
                // setRealTimeNotification={setRealTimeNotification}
              />
              <Dropdown className="logout-dropdown" ref={dropdownRef}>
                <Dropdown.Toggle
                  id="dropdown-basic"
                  as={CustomToggle}
                  ref={dropdownRef}
                  className="rounded-circle transparent-dropdown-toggle"
                >
                  {userInformation?.s3Key ? (
                    <Image
                      src={userInformation.s3Key || "/ProfilePic.png"}
                      roundedCircle
                      style={{
                        cursor: "pointer",
                        height: "40px",
                        width: "40px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <FaUser color="grey" style={{ cursor: "pointer" }} />
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {
                    <Dropdown.Item
                      onClick={() =>
                        navigate(
                          `/${
                            roleOfTheUser === "Student" ? "student" : "user"
                          }/profile`
                        )
                      }
                    >
                      View Profile
                    </Dropdown.Item>
                  }
                  <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>
        </div>
      </Navbar>
      <Navbar
        style={{ height: "63px", color: "black" }}
        className={`justify-content-between top-screen-navbar ${
          !isMobileView && "d-md-none"
        }`}
      >
        <div className="d-flex justify-content-between w-100 align-items-center">
          <Col sm={8} className="px-4 d-flex align-items-center">
            <MdMenu
              onClick={showSidebar}
              style={{ cursor: "pointer", marginRight: "16px" }}
              size={20}
            />
            <div>
              {navbar?.text ? (
                getMobileNavbarHeading(navbar?.text, navbar?.params)
              ) : (
                <h3
                  className={`mb-0 top-heading header-title`}
                  style={{ fontSize: "20px", color: "#1D2026" }}
                >
                  {getCurrentGreetingMessage()}!
                </h3>
              )}
            </div>
          </Col>
          <Col sm={4}>
            <div className="profile_main_div" style={{ marginRight: "0.5rem" }}>
              <div
                className="d-flex align-items-center justify-content-center"
                style={
                  {
                    // backgroundColor: "#F5F7FA",
                    // height: "48px",
                    // width: "80px",
                  }
                }
              >
                <NotificationPopover
                  isMobileView={isMobileView}
                  // realTimeNotification={realTimeNotification}
                  // setRealTimeNotification={setRealTimeNotification}
                />
                <div
                  style={{
                    backgroundColor: "#F5F7FA",
                    height: "40px",
                    width: "49px",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Dropdown className="" ref={dropdownRef}>
                    <Dropdown.Toggle
                      id="dropdown-basic"
                      as={CustomToggle}
                      ref={dropdownRef}
                      className="rounded-circle transparent-dropdown-toggle"
                    >
                      {userInformation?.s3Key ? (
                        <Image
                          src={userInformation.s3Key || "/ProfilePic.png"}
                          roundedCircle
                          style={{
                            cursor: "pointer",
                            height: "30px",
                            width: "30px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <FaUser
                          color="grey"
                          size={24}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu align="end">
                      {roleOfTheUser === "Student" && (
                        <Dropdown.Item
                          onClick={() =>
                            navigate(
                              `/${
                                roleOfTheUser === "Student" ? "student" : "user"
                              }/profile`
                            )
                          }
                        >
                          View Profile
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item onClick={handleLogout}>
                        Logout
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
          </Col>
        </div>
      </Navbar>
      <SubscriptionModal
        show={showModal}
        handleClose={handleModalClose}
        classroomId={classroomId}
        subscription={subscription} // Pass subscription data
        initialRemainingTime={remainingTime} // Pass calculated remaining time
      />
      {/* Responsive font size for header, subscription box, and navbar headings */}
      <style>{`
        @media (max-width: 1024px) {
          .header-title {
            font-size: 15px !important;
          }
          .subscription-box-width {
            font-size: 11px !important;
            min-width: 90px !important;
            max-width: 120px !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
          .navbar-greeting {
            font-size: 11px !important;
          }
          .navbar-heading-title {
            font-size: 15px !important;
          }
        }
      `}</style>
    </>
  );
};

export default memo(Header);
